import React from "react";
import { Alert, Box, Button, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import type {
  GlobalOutputImageId,
  OutputImage,
  OutputImageRole as OutputImageRoleType,
  SpecsLayer,
  SpecsLayerModelPath,
} from "./types";
import { OutputImageRole } from "./types";
import {
  getOutputImageSide,
  getOutputImageSideNames,
  getOutputPreviewUrl,
} from "../../common/slices/specsEditor/reducers/images";
import { EditableTitle } from "./SpecsEditorFields/index";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import { CompositePreviewSection } from "./SpecsCompositePreviewSection";
import type { LayerModel } from "./specsModel/LayerModel";
import type { GeneratorSideModel } from "./specsModel/GeneratorSideModel";
import { clearAssetPreviewValue, getAssetPreviewValue } from "./specsAssetUrls";
import {
  buildSpecsLayerImageFieldPath,
  buildSpecsLayerImageFileNameBase,
  slugFileSegment,
  type SpecsLayerImageUploadIdentity,
} from "./specsImageUpload";
import { SpecsImageEditor } from "./SpecsImageEditor";
import { buildModelImageSourceOptions } from "./specsImageSourceOptions";
import type { VersionModel } from "./specsModel/VersionModel";

const COMMON_LAYER_IMAGE_SIDE_NAMES = ["front", "back"];
type LayerImageRole = Extract<
  OutputImageRoleType,
  typeof OutputImageRole.Mask | typeof OutputImageRole.Highlight
>;

function hasGlobalHighlight(
  globalOutputImages: Record<GlobalOutputImageId, OutputImage>
) {
  return Boolean(
    getOutputPreviewUrl(globalOutputImages.globalHighlightFront) ||
      getOutputPreviewUrl(globalOutputImages.globalHighlightBack)
  );
}

function hasLayerHighlightImage(layer: LayerModel | null) {
  return Boolean(layer?.sides.some((side) => side.highlightUrl));
}

function getModelOutputUrl(side: GeneratorSideModel, role: LayerImageRole) {
  return role === OutputImageRole.Mask ? side.imageUrl : side.highlightUrl;
}

function getModelOutputPreviewUrl(
  side: GeneratorSideModel,
  role: LayerImageRole
) {
  return role === OutputImageRole.Mask
    ? getAssetPreviewValue(side.extra, "imageUrl", null)
    : getAssetPreviewValue(side.extra, "highlightUrl", null);
}

function getModelOutputDebugPath(
  side: GeneratorSideModel,
  role: LayerImageRole
) {
  const sideDebugPath = side._debugPath;
  if (!sideDebugPath) {
    return undefined;
  }

  return `${sideDebugPath}.${role === OutputImageRole.Mask ? "imageUrl" : "highlightUrl"}`;
}

function persistModelLayerImageUrl(
  layer: LayerModel,
  role: LayerImageRole,
  sideName: string,
  url: string | null
) {
  const side = layer.sides.find((candidate) => candidate.name === sideName);
  if (!side) {
    return;
  }

  if (role === OutputImageRole.Mask) {
    side.imageUrl = url ?? "";
    if (!url) {
      clearAssetPreviewValue(side.extra, "imageUrl");
    }
  } else {
    side.highlightUrl = url ?? "";
    if (!url) {
      clearAssetPreviewValue(side.extra, "highlightUrl");
    }
  }
}

function getModelEditorSides(
  modelLayer: LayerModel,
  role: LayerImageRole,
  activeEmptySideNames: Set<string>
) {
  const sidesWithImages = modelLayer.sides.filter(
    (side) =>
      getModelOutputUrl(side, role) ||
      getModelOutputPreviewUrl(side, role) ||
      activeEmptySideNames.has(side.name)
  );
  return sidesWithImages.length > 0 ? sidesWithImages : modelLayer.sides;
}

function getAddableSideNames(editorSides: GeneratorSideModel[]) {
  const visibleSideNames = new Set(editorSides.map((side) => side.name));
  return COMMON_LAYER_IMAGE_SIDE_NAMES.filter(
    (sideName) => !visibleSideNames.has(sideName)
  );
}

function getModelLayerPathKey(modelLayerPath: SpecsLayerModelPath) {
  return [
    modelLayerPath.partName,
    modelLayerPath.elementName,
    modelLayerPath.styleName,
    modelLayerPath.layerName,
  ].join(".");
}

function ModelLayerImageEditors({
  modelLayer,
  modelLayerPath,
  imageRole,
  currentVersion,
  productName,
}: {
  modelLayer: LayerModel;
  modelLayerPath: SpecsLayerModelPath;
  imageRole: LayerImageRole;
  currentVersion: VersionModel | null | undefined;
  productName: string | undefined;
}) {
  const [activeEmptySideNames, setActiveEmptySideNames] = React.useState<
    Set<string>
  >(() => new Set());
  const modelLayerKey = getModelLayerPathKey(modelLayerPath);
  const modelEditorSides = getModelEditorSides(
    modelLayer,
    imageRole,
    activeEmptySideNames
  );
  const addableModelSideNames = getAddableSideNames(modelEditorSides);
  const sourceOptionsForSide = (sideName: string) =>
    buildModelImageSourceOptions(currentVersion, sideName);

  React.useEffect(() => {
    setActiveEmptySideNames(new Set());
  }, [imageRole, modelLayerKey]);

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
          gap: 2,
        }}
      >
        {modelEditorSides.map((side) => {
          const uploadIdentity = {
            ...modelLayerPath,
            layerName: modelLayer.name,
            role: imageRole,
            side: side.name,
          } satisfies SpecsLayerImageUploadIdentity;

          return (
            <SpecsImageEditor
              key={`${imageRole}:${side.name}`}
              label={`${imageRole === OutputImageRole.Mask ? "Mask" : "Highlight"} ${side.name}`}
              value={
                getModelOutputUrl(side, imageRole) ||
                getModelOutputPreviewUrl(side, imageRole)
              }
              role={imageRole}
              fileNamePrefix={buildSpecsLayerImageFileNameBase(uploadIdentity)}
              sourceOptions={sourceOptionsForSide(side.name)}
              debugPath={getModelOutputDebugPath(side, imageRole)}
              uploadContext={{
                specType: "product",
                specName: productName,
                fieldPath: buildSpecsLayerImageFieldPath(uploadIdentity),
              }}
              onChange={(url) => {
                persistModelLayerImageUrl(
                  modelLayer,
                  imageRole,
                  side.name,
                  url
                );
                if (!url) {
                  setActiveEmptySideNames((current) => {
                    const next = new Set(current);
                    next.delete(side.name);
                    return next;
                  });
                }
              }}
            />
          );
        })}
      </Box>
      {addableModelSideNames.length > 0 ? (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {addableModelSideNames.map((sideName) => (
            <Button
              key={sideName}
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                modelLayer.ensureSide(sideName);
                setActiveEmptySideNames((current) => {
                  const next = new Set(current);
                  next.add(sideName);
                  return next;
                });
              }}
            >
              Add {sideName} image
            </Button>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}

export function LayerCard({ layer }: { layer: SpecsLayer }) {
  const {
    editor,
    currentVersion,
    effectiveLayerView: layerView,
  } = useSpecsEditorContextValue();
  const modelLayer = layer.modelLayerPath
    ? (currentVersion?.getLayer(layer.modelLayerPath)?.layer ?? null)
    : null;
  const layerName = modelLayer?.layerLabel || modelLayer?.name || layer.name;
  const showComposite = layerView === "preview" || layerView === "global";
  const showGlobalHighlightNotice =
    layerView === "highlight" &&
    !hasLayerHighlightImage(modelLayer) &&
    hasGlobalHighlight(editor.globalOutputImages);
  const imageRole =
    layerView === "highlight"
      ? OutputImageRole.Highlight
      : OutputImageRole.Mask;
  const runtimeOutputImage =
    imageRole === OutputImageRole.Highlight ? layer.highlight : layer.mask;
  const sourceOptionsForSide = (sideName: string) =>
    buildModelImageSourceOptions(currentVersion, sideName);

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <EditableTitle
          value={layer.name}
          placeholder="Layer name"
          onChange={(name) => editor.updateLayer(layer.id, { name })}
          variant="h2"
        />
      </Stack>

      {showGlobalHighlightNotice && (
        <Alert severity="info" sx={{ mb: 2 }}>
          A global highlight is in effect for this product.
        </Alert>
      )}

      {!showComposite && modelLayer && layer.modelLayerPath && (
        <ModelLayerImageEditors
          modelLayer={modelLayer}
          modelLayerPath={layer.modelLayerPath}
          imageRole={imageRole}
          currentVersion={currentVersion}
          productName={editor.identity.productName || undefined}
        />
      )}

      {!showComposite && !modelLayer && runtimeOutputImage && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: 2,
          }}
        >
          {getOutputImageSideNames(runtimeOutputImage).map((sideName) => {
            const side = getOutputImageSide(runtimeOutputImage, sideName);
            if (!side) {
              return null;
            }

            return (
              <SpecsImageEditor
                key={`${imageRole}:${sideName}`}
                label={`${imageRole === OutputImageRole.Mask ? "Mask" : "Highlight"} ${sideName}`}
                value={side.outputImageUrl}
                role={imageRole}
                fileNamePrefix={[
                  slugFileSegment(layerName),
                  imageRole,
                  slugFileSegment(sideName),
                ].join("-")}
                sourceOptions={sourceOptionsForSide(sideName)}
                uploadContext={{
                  specType: "product",
                  specName: editor.identity.productName || undefined,
                }}
                onChange={(url) => {
                  if (url) {
                    editor.setProcessedImageUrl(
                      runtimeOutputImage.id,
                      url,
                      sideName
                    );
                  }
                }}
              />
            );
          })}
        </Box>
      )}

      {showComposite && <CompositePreviewSection />}
    </Box>
  );
}
